import React from 'react';
import { useInviteCollaboratorWithDefaultValues } from './useInviteCollaboratorWithDefaultValues';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { getIntl } from '../../../../../utils/intl';
import { useIsProfileOwner } from '../../../../v2BaseComponents/Permissions/hooks';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { useCollaborators } from '../../../../../hooks/api/useCollaborators';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import useCareTeamMember from '../../../../../hooks/api/useCareTeamMember';
import CareTeamCollaborators from './CareTeamCollaborators';
import { ContentStack } from '../../../../v2BaseComponents/Stack';
import RelatedCareTeam from './RelatedCareTeam';
import { isInsurance } from '../../../../../utils/careTeamUtils';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { useDeleteCareTeamMemberButton } from './useDeleteCareTeamMemberButton';
import CareTeamMemberDetails from '../../../../shared/CareTeam/CareTeamMemberDetails';
import InsuranceCards from '../../../../shared/CareTeam/InsuranceCards';

const intl = getIntl('careTeamMemberDetailsTab', {
    collaborators: 'Care Team Collaborators',
    add: 'Add',
    invite: 'Invite Collaborator',
    addRelatedCareTeamMember: 'Add Related Care Team Member',
    edit: 'Edit',
    copy: 'Duplicate',
    delete: 'Delete',
});

export type DetailsTabOwnProps = {
    careTeamMemberId: string;
    profileId: string;
};

export type DetailsTabProps = DetailsTabOwnProps;

const DetailsTab: React.FC<DetailsTabProps> = ({ careTeamMemberId, profileId }) => {
    const navigation = useProfilesTabNavigation();
    const { careTeamCollaborators, isLoadingCareTeamCollaborators } = useCollaborators({
        careTeamMemberId,
        medicalProfileId: profileId,
    });
    const { relatedCareTeamMembers, isLoadingRelatedCareTeamMembers } = useCareTeamMember({
        careTeamMemberId,
        medicalProfileId: profileId,
    });
    const canEditCareTeamMember = useHasPermission(profileId, 'careTeam', 'edit');
    const { inviteCareTeamCollaborator, isDisabled } = useInviteCollaboratorWithDefaultValues({
        careTeamMemberId,
        profileId,
    });
    const { isLoadingCareTeamMember, careTeamMember } = useCareTeamMember({
        medicalProfileId: profileId,
        careTeamMemberId: careTeamMemberId,
    });
    const deleteCareTeamMemberButton = useDeleteCareTeamMemberButton({
        medicalProfileId: profileId,
        careTeamMemberId: careTeamMemberId,
    });
    const isProfileOwner = useIsProfileOwner(profileId);

    return (
        <>
            <AnimateLoadedContent
                isLoading={
                    isLoadingCareTeamCollaborators ||
                    isLoadingCareTeamMember ||
                    isLoadingCareTeamMember ||
                    isLoadingRelatedCareTeamMembers
                }
            >
                {() => (
                    <ActionButtonLayout
                        scrollable
                        whiteBackground
                        actions={[
                            {
                                title: intl('edit'),
                                icon: 'Edit2',
                                onPress: () => {
                                    navigation.navigate('edit-care-team-member', {
                                        profileId: profileId,
                                        careTeamMemberId: careTeamMemberId,
                                    });
                                },
                                show: canEditCareTeamMember,
                            },
                            {
                                title: intl('copy'),
                                icon: 'Copy',
                                onPress: () => {
                                    navigation.navigate('copy-care-team-member', {
                                        profileId: profileId,
                                        careTeamMemberId: careTeamMemberId,
                                    });
                                },
                            },
                            {
                                icon: 'Trash',
                                title: intl('delete'),
                                onPress: deleteCareTeamMemberButton.onPress,
                                show: canEditCareTeamMember,
                                isDisabled: deleteCareTeamMemberButton.isDeletingCareTeamMember,
                            },
                            {
                                title: intl('invite'),
                                icon: 'UserPlus',
                                onPress: inviteCareTeamCollaborator,
                                isDisabled: !isProfileOwner || isDisabled,
                                show: isProfileOwner,
                            },
                            {
                                title: intl('addRelatedCareTeamMember'),
                                icon: 'UserPlus',
                                onPress: () => {
                                    navigation.navigate('add-related-care-team-member', {
                                        profileId: profileId,
                                        careTeamMemberId: careTeamMemberId,
                                    });
                                },
                                isDisabled: isDisabled,
                                show: canEditCareTeamMember,
                            },
                        ]}
                    >
                        <MainContentContainer>
                            <ContentStack>
                                {careTeamMember ? <CareTeamMemberDetails careTeamMember={careTeamMember} /> : null}
                                <CareTeamCollaborators
                                    careTeamCollaborators={careTeamCollaborators}
                                    profileId={profileId}
                                />
                                {!isLoadingCareTeamMember && !!careTeamMember && isInsurance(careTeamMember) ? (
                                    <InsuranceCards
                                        careTeamMemberId={careTeamMemberId}
                                        source={{
                                            source: 'profile',
                                            medicalProfileId: profileId,
                                        }}
                                    />
                                ) : null}
                                <RelatedCareTeam
                                    careTeamMemberId={careTeamMemberId}
                                    profileId={profileId}
                                    relatedCareTeamMembers={relatedCareTeamMembers}
                                />
                            </ContentStack>
                        </MainContentContainer>
                    </ActionButtonLayout>
                )}
            </AnimateLoadedContent>
            {deleteCareTeamMemberButton.Prompt}
        </>
    );
};

export default React.memo(DetailsTab);
